import { TextField, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useState, useEffect } from "react";
import {
  parseTimeRangeString,
  fromRFC3339,
  isNow,
  isRelativeTime,
  isRFC3339Date,
} from "../../utils/time";
import { useTimeRangeFromQuery, type UseTimeRangeOptions } from "../../hooks/useRouteParams";

export interface TimeRangeFilterBarProps {
  options?: UseTimeRangeOptions;
}

/**
 * Formats a date to human-readable format like "6 jan 26 14:03"
 */
function formatHumanDate(date: Date): string {
  const day = date.getDate();
  const monthNames = [
    "jan",
    "feb",
    "mar",
    "apr",
    "may",
    "jun",
    "jul",
    "aug",
    "sep",
    "oct",
    "nov",
    "dec",
  ];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  const yearShort = year % 100;
  const hour = date.getHours().toString().padStart(2, "0");
  const minute = date.getMinutes().toString().padStart(2, "0");
  return `${day} ${month} ${yearShort} ${hour}:${minute}`;
}

/**
 * Formats duration in milliseconds to string like "1d", "5m", etc.
 */
function formatDuration(ms: number): string | null {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d`;
  if (hours > 0) return `${hours}h`;
  if (minutes > 0) return `${minutes}m`;
  if (seconds > 0) return `${seconds}s`;
  return null;
}

/**
 * Converts query parameters back to a human-readable time range string
 */
function formatTimeRangeFromQuery(fromStr: string | null, stopStr: string | null): string {
  // If from is relative and stop is now or missing, return just the relative time
  if (fromStr && isRelativeTime(fromStr) && (!stopStr || isNow(stopStr))) {
    return fromStr;
  }

  // If both are RFC3339 dates, format as date range
  if (fromStr && stopStr && isRFC3339Date(fromStr) && isRFC3339Date(stopStr)) {
    const fromDate = fromRFC3339(fromStr);
    const stopDate = fromRFC3339(stopStr);

    if (fromDate && stopDate) {
      // Round to nearest minute (remove seconds/milliseconds)
      const fromRounded = new Date(fromDate);
      fromRounded.setSeconds(0, 0);
      const stopRounded = new Date(stopDate);
      stopRounded.setSeconds(0, 0);

      // Check if stop is from + duration
      const durationMs = stopRounded.getTime() - fromRounded.getTime();
      const durationStr = formatDuration(durationMs);
      if (durationStr) {
        // Try to format as "date+duration"
        const dateStr = formatHumanDate(fromRounded);
        return `${dateStr}+${durationStr}`;
      }

      // Format as "date1 - date2"
      const fromFormatted = formatHumanDate(fromRounded);
      const stopFormatted = formatHumanDate(stopRounded);
      return `${fromFormatted} - ${stopFormatted}`;
    }
  }

  // Fallback: show raw values or default to "5m"
  if (fromStr && stopStr) {
    return `${fromStr} - ${stopStr}`;
  }
  if (fromStr) {
    return fromStr;
  }
  // Default: no parameters means 5m (from = 5m, stop = now)
  return "5m";
}

export function TimeRangeFilterBar({ options }: TimeRangeFilterBarProps) {
  const theme = useTheme();
  const { setCustomRange, rangeFromStr, rangeStopStr } = useTimeRangeFromQuery(options);
  const [inputValue, setInputValue] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  // Sync inputValue with query parameters
  useEffect(() => {
    const formatted = formatTimeRangeFromQuery(rangeFromStr, rangeStopStr);
    setInputValue(formatted);
  }, [rangeFromStr, rangeStopStr]);

  const handleChange = (value: string) => {
    setInputValue(value);
    setError(null);
  };

  const handleBlur = () => {
    if (!inputValue.trim()) {
      // Clear the range if input is empty
      setCustomRange(null, null);
      return;
    }

    const parsed = parseTimeRangeString(inputValue);
    if (parsed) {
      setCustomRange(parsed.from, parsed.stop);
      setError(null);
    } else {
      setError(
        "Invalid format. Examples: '5m', '6 jan 26 14:03+1d', '6 jan 26 14:03 - 7 jan 26 14:03'",
      );
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleBlur();
    }
  };

  return (
    <TextField
      size="small"
      label="Time Range"
      placeholder='e.g., "5m", "6 jan 26 14:03+1d", "6 jan 26 14:03 - 7 jan 26 14:03"'
      value={inputValue}
      onChange={(e) => handleChange(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      error={!!error}
      sx={{
        minWidth: 400,
        "& .MuiOutlinedInput-root": {
          bgcolor: theme.palette.background.paper,
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: alpha(theme.palette.text.primary, 0.16),
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: alpha(theme.palette.text.primary, 0.28),
        },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.primary.main,
        },
      }}
    />
  );
}
